import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of, throwError } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { catchError, map, switchMap, take, tap } from "rxjs/operators";
import { environment } from "environments/environment";
import { BaseRespone, PaginationResult } from "app/core/models/common.response";
import { ApprovedMeasurement } from "./approved-measurement.types";

@Injectable({
    providedIn: 'root'
})
export class ApprovedMeasurementsService {
    // Private
    private _approvedMeasurement: BehaviorSubject<ApprovedMeasurement | null> = new BehaviorSubject(null);
    private _approvedMeasurements: BehaviorSubject<ApprovedMeasurement[] | null> = new BehaviorSubject(null);
    private _builtUpApprovedMeasurements: BehaviorSubject<ApprovedMeasurement[] | null> = new BehaviorSubject(null);
    private _landUpApprovedMeasurements: BehaviorSubject<ApprovedMeasurement[] | null> = new BehaviorSubject(null);
    private _carpetAreaApprovedMeasurements: BehaviorSubject<ApprovedMeasurement[] | null> = new BehaviorSubject(null);
    constructor(private _httpClient: HttpClient) {
    }

    /**
     * Getter for approvedMeasurement
     */
    get approvedMeasurement$(): Observable<ApprovedMeasurement> {
        return this._approvedMeasurement.asObservable();
    }

    /**
     * Getter for approvedMeasurements
     */
    get approvedMeasurements$(): Observable<ApprovedMeasurement[]> {
        return this._approvedMeasurements.asObservable();
    }

    get builtUpApprovedMeasurements$(): Observable<ApprovedMeasurement[]> {
        return this._builtUpApprovedMeasurements.asObservable();
    }

    get landUpApprovedMeasurements$(): Observable<ApprovedMeasurement[]> {
        return this._landUpApprovedMeasurements.asObservable();
    }

    get carpetAreaApprovedMeasurements$(): Observable<ApprovedMeasurement[]> {
        return this._carpetAreaApprovedMeasurements.asObservable();
    }
    /**
     * Get approvedMeasurements
     */
    getApprovedMeasurements(reportId: number): Observable<BaseRespone<ApprovedMeasurement[]>> {
        return this._httpClient.get<BaseRespone<ApprovedMeasurement[]>>(`${environment.baseUrl}/Report/GetApprovedMeasurementlist/${reportId}`).pipe(
            tap((response) => {
                this._approvedMeasurements.next(response.result);
            })
        );
    }

    getLandUpApprovedMeasurements(reportId: number): Observable<BaseRespone<ApprovedMeasurement[]>> {
        return this._httpClient.get<BaseRespone<ApprovedMeasurement[]>>(`${environment.baseUrl}/Report/GetApprovedMeasurementlist/${reportId}?type=Land Area`).pipe(
            tap((response) => {
                this._landUpApprovedMeasurements.next(response.result);
            })
        );
    }

    getBuiltUpApprovedMeasurements(reportId: number): Observable<BaseRespone<ApprovedMeasurement[]>> {
        return this._httpClient.get<BaseRespone<ApprovedMeasurement[]>>(`${environment.baseUrl}/Report/GetApprovedMeasurementlist/${reportId}?type=BuiltUp Area&type=Permissble Built up area as per prevailing building bye laws`).pipe(
            tap((response) => {
                this._builtUpApprovedMeasurements.next(response.result);
            })
        );
    }

    getCarpetApprovedMeasurements(reportId: number): Observable<BaseRespone<ApprovedMeasurement[]>> {
        return this._httpClient.get<BaseRespone<ApprovedMeasurement[]>>(`${environment.baseUrl}/Report/GetApprovedMeasurementlist/${reportId}?type=Carpet Area`).pipe(
            tap((response) => {
                this._carpetAreaApprovedMeasurements.next(response.result);
            })
        );
    }



    /**
      * Create approvedMeasurement
      */

    createApprovedMeasurement(approvedMeasurement: ApprovedMeasurement, type?: string): Observable<BaseRespone<ApprovedMeasurement>> {
        switch (type) {
            case 'Land Area':
                return this.landUpApprovedMeasurements$.pipe(
                    take(1),
                    switchMap(approvedMeasurements => this._httpClient.post<BaseRespone<ApprovedMeasurement>>(`${environment.baseUrl}/Report/AddApprovedMeasurement`, approvedMeasurement)
                        .pipe(
                            catchError((error) => {
                                return of(error.error);
                            }),
                            map((response) => {
                                // Return the new product
                                if (response.isSuccess) {
                                    const currentMeasurements = this._landUpApprovedMeasurements.getValue();

                                    // Combine new result with existing values
                                    const combined = [response.result, ...currentMeasurements];

                                    // Filter out items where id === 0
                                    const filtered = combined.filter(item => item.id !== 0 && item.type == approvedMeasurement.type);

                                    // Update BehaviorSubject with filtered array
                                    this._landUpApprovedMeasurements.next(filtered);
                                }
                                return response;
                            })
                        ))
                );
            case 'Builtup Area':
            case 'Permissble Built up area as per prevailing building bye laws':
                return this.builtUpApprovedMeasurements$.pipe(
                    take(1),
                    switchMap(siteMeasurements => this._httpClient.post<BaseRespone<ApprovedMeasurement>>(`${environment.baseUrl}/Report/AddApprovedMeasurement`, approvedMeasurement)
                        .pipe(
                            catchError((error) => {
                                return of(error.error);
                            }),
                            map((response) => {
                                // Return the new product
                                if (response.isSuccess) {
                                    const currentMeasurements = this._builtUpApprovedMeasurements.getValue();

                                    // Combine new result with existing values
                                    const combined = [response.result, ...currentMeasurements];

                                    // Filter out items where id === 0
                                    // const filtered = combined.filter(item => item.id !== 0 && item.type == approvedMeasurement.type);
                                    const filtered = combined.filter(item => item.id !== 0);
                                    // Update BehaviorSubject with filtered array
                                    this._builtUpApprovedMeasurements.next(filtered);
                                }
                                return response;
                            })
                        ))
                );
            case 'Carpet Area':
                return this.carpetAreaApprovedMeasurements$.pipe(
                    take(1),
                    switchMap(siteMeasurements => this._httpClient.post<BaseRespone<ApprovedMeasurement>>(`${environment.baseUrl}/Report/AddApprovedMeasurement`, approvedMeasurement)
                        .pipe(
                            catchError((error) => {
                                return of(error.error);
                            }),
                            map((response) => {
                                // Return the new product
                                if (response.isSuccess) {
                                    const currentMeasurements = this._carpetAreaApprovedMeasurements.getValue();

                                    // Combine new result with existing values
                                    const combined = [response.result, ...currentMeasurements];

                                    // Filter out items where id === 0
                                    const filtered = combined.filter(item => item.id !== 0 && item.type == approvedMeasurement.type);

                                    // Update BehaviorSubject with filtered array
                                    this._carpetAreaApprovedMeasurements.next(filtered);
                                }
                                return response;
                            })
                        ))
                );
        }
    }

    addNewApprovedMeasurement(reportId: number, selectedType?: string) {
        switch (selectedType) {
            case 'Land Area': {
                const currentMeasurements = this._landUpApprovedMeasurements.getValue();
                const newMeasurement: ApprovedMeasurement = {
                    id: 0,
                    reportId: reportId,
                    type: selectedType,
                    particular: '',
                    width: 0,
                    depth: 0,
                    total: 0,
                    approvedArea: 0,
                    approvedBuiltUp: 0,
                    approvedSuperBuiltUp: 0,
                    orderIndex: currentMeasurements.length + 1,
                    selected: false,
                    isEditing: true,
                    selectedType: selectedType,
                };

                this._landUpApprovedMeasurements.next([...currentMeasurements, newMeasurement]);
                break;
            }

            case 'Builtup Area': {
                const currentMeasurements = this._builtUpApprovedMeasurements.getValue();
                const newMeasurement: ApprovedMeasurement = {
                    id: 0,
                    reportId: reportId,
                    type: selectedType,
                    particular: '',
                    width: 0,
                    depth: 0,
                    total: 0,
                    approvedArea: 0,
                    approvedBuiltUp: 0,
                    approvedSuperBuiltUp: 0,
                    orderIndex: currentMeasurements.length + 1,
                    selected: false,
                    isEditing: true,
                    selectedType: selectedType,
                };

                this._builtUpApprovedMeasurements.next([...currentMeasurements, newMeasurement]);
                break;
            }

            case 'Carpet Area': {
                const currentMeasurements = this._carpetAreaApprovedMeasurements.getValue();
                const newMeasurement: ApprovedMeasurement = {
                    id: 0,
                    reportId: reportId,
                    type: selectedType,
                    particular: '',
                    width: 0,
                    depth: 0,
                    total: 0,
                    approvedArea: 0,
                    approvedBuiltUp: 0,
                    approvedSuperBuiltUp: 0,
                    orderIndex: currentMeasurements.length + 1,
                    selected: false,
                    isEditing: true,
                    selectedType: selectedType,
                };

                this._carpetAreaApprovedMeasurements.next([...currentMeasurements, newMeasurement]);
                break;
            }
        }
    }

    cancelLandRow(index: number): void {
        const current = this._landUpApprovedMeasurements.getValue();

        if (!current[index]?.particular?.trim()) {
            const updated = current.filter((_, i) => i !== index);
            this._landUpApprovedMeasurements.next(updated);
        } else {
            current[index].isEditing = false;
            this._landUpApprovedMeasurements.next([...current]);
        }
    }

    cancelBuiltRow(index: number): void {
        const current = this._builtUpApprovedMeasurements.getValue();

        if (!current[index]?.particular?.trim()) {
            const updated = current.filter((_, i) => i !== index);
            this._builtUpApprovedMeasurements.next(updated);
        } else {
            current[index].isEditing = false;
            this._builtUpApprovedMeasurements.next([...current]);
        }
    }


    cancelCarpetRow(index: number): void {
        const current = this._carpetAreaApprovedMeasurements.getValue();

        if (!current[index]?.particular?.trim()) {
            const updated = current.filter((_, i) => i !== index);
            this._carpetAreaApprovedMeasurements.next(updated);
        } else {
            current[index].isEditing = false;
            this._carpetAreaApprovedMeasurements.next([...current]);
        }
    }



    /**
     * Get board
     *
     * @param id
     */
    getApprovedMeasurement(id: number): Observable<BaseRespone<ApprovedMeasurement>> {
        return this._httpClient.get(`${environment.baseUrl}/Report/GetApprovedMeasurement/${id}`).pipe(
            catchError((error) => {
                return of(error.error);
            }),
            map(response => response),
            tap(board => this._approvedMeasurement.next(board.result))
        );
    }


    /**
    * Get contact by id
    */
    getApprovedMeasurementById(id: number): Observable<ApprovedMeasurement> {
        return this._approvedMeasurements.pipe(
            take(1),
            map((approvedMeasurements) => {

                // Find the contact
                const approvedMeasurement = approvedMeasurements.find(item => item.id === id) || null;

                // Update the approvedMeasurement
                this._approvedMeasurement.next(approvedMeasurement);

                // Return the approvedMeasurement
                return approvedMeasurement;
            }),
            switchMap((approvedMeasurement) => {

                if (!approvedMeasurement) {
                    return throwError('Could not found approvedMeasurement with id of ' + id + '!');
                }

                return of(approvedMeasurement);
            })
        );
    }

    /**
     * Update the card
     *
     * @param id
     * @param card
     */

    updateApprovedMeasurement(
        approvedMeasurement: ApprovedMeasurement,
        type?: string
    ): Observable<BaseRespone<ApprovedMeasurement>> {
        const measurementMap: Record<string, BehaviorSubject<ApprovedMeasurement[]>> = {
            'Land Area': this._landUpApprovedMeasurements,
            'Builtup Area':  this._builtUpApprovedMeasurements,
            'Permissble Built up area as per prevailing building bye laws':  this._builtUpApprovedMeasurements,
            'Carpet Area': this._carpetAreaApprovedMeasurements
        };

        const subjectToUpdate = measurementMap[type ?? ''];

        if (!subjectToUpdate) {
            return throwError(() => new Error('Invalid approved measurement type'));
        }

        return subjectToUpdate.pipe(
            take(1),
            switchMap((approvedMeasurementsRaw) => {
                const approvedMeasurements = Array.isArray(approvedMeasurementsRaw)
                    ? approvedMeasurementsRaw
                    : [];

                return this._httpClient
                    .put<BaseRespone<ApprovedMeasurement>>(
                        `${environment.baseUrl}/Report/UpdateApprovedMeasurement`,
                        approvedMeasurement
                    )
                    .pipe(
                        map((response) => {
                            if (response?.isSuccess) {
                                const updated = response.result;
                                const index = approvedMeasurements.findIndex(item => item.id === updated.id);

                                if (index !== -1) {
                                    approvedMeasurements[index] = updated;
                                    subjectToUpdate.next([...approvedMeasurements]); // emit updated array
                                }
                            }

                            return response;
                        }),
                        catchError((error) => {
                            console.error('Error updating approved measurement:', error);
                            return of({
                                isSuccess: false,
                                result: null,
                                errors: [error?.message || 'Unknown error occurred']
                            } as BaseRespone<ApprovedMeasurement>);
                        })
                    );
            })
        );
    }


    deleteApprovedMeasurement(id: number, type?: string): Observable<BaseRespone<boolean>> {
        const measurementMap: Record<string, {
            observable$: Observable<ApprovedMeasurement[]>,
            subject: BehaviorSubject<ApprovedMeasurement[]>
        }> = {
            "Land Area": {
                observable$: this.landUpApprovedMeasurements$,
                subject: this._landUpApprovedMeasurements
            },
            "Builtup Area": {
                observable$: this.builtUpApprovedMeasurements$,
                subject: this._builtUpApprovedMeasurements
            },
            "Permissble Built up area as per prevailing building bye laws": {
                observable$: this.builtUpApprovedMeasurements$,
                subject: this._builtUpApprovedMeasurements
            },
            "Carpet Area": {
                observable$: this.carpetAreaApprovedMeasurements$,
                subject: this._carpetAreaApprovedMeasurements
            }
        };

        const measurementEntry = measurementMap[type ?? ""];

        if (!measurementEntry) {
            return throwError(() => new Error("Invalid approved measurement type"));
        }

        return measurementEntry.observable$.pipe(
            take(1),
            switchMap(approvedMeasurements =>
                this._httpClient.delete<BaseRespone<boolean>>(`${environment.baseUrl}/Report/DeleteApprovedMeasurement/${id}`).pipe(
                    map(deleted => {
                        const updatedMeasurements = approvedMeasurements.filter(item => item.id !== id);
                        measurementEntry.subject.next(updatedMeasurements);
                        return deleted;
                    })
                )
            )
        );
    }


    deleteAllApprovedMeasurement(ids: number[], type?: string): Observable<BaseRespone<boolean>> {
        const requestBody = { ids };

        const mapping: Record<string, {
            stream$: Observable<ApprovedMeasurement[]>,
            subject: BehaviorSubject<ApprovedMeasurement[]>
        }> = {
            'Land Area': {
                stream$: this.landUpApprovedMeasurements$,
                subject: this._landUpApprovedMeasurements
            },
            'Builtup Area': {
                stream$: this.builtUpApprovedMeasurements$,
                subject: this._builtUpApprovedMeasurements
            },
            'Permissble Built up area as per prevailing building bye laws': {
                stream$: this.builtUpApprovedMeasurements$,
                subject: this._builtUpApprovedMeasurements
            },
            'Carpet Area': {
                stream$: this.carpetAreaApprovedMeasurements$,
                subject: this._carpetAreaApprovedMeasurements
            }
        };

        const selected = mapping[type || ''];

        if (!selected) {
            const fallback: BaseRespone<boolean> = {
                isSuccess: false,
                message: 'Invalid type',
                errors: ['Invalid type'],
                result: false
            };
            return of(fallback);
        }

        return selected.stream$.pipe(
            take(1),
            switchMap(approvedMeasurements =>
                this._httpClient.delete<BaseRespone<boolean>>(
                    `${environment.baseUrl}/Report/DeleteAllApprovedMeasurement`,
                    { body: requestBody }
                ).pipe(
                    map((deletedResponse) => {
                        const updatedMeasurements = approvedMeasurements.filter(am => !ids.includes(am.id));
                        selected.subject.next(updatedMeasurements);
                        return deletedResponse;
                    })
                )
            )
        );
    }
}
